/**
 * cardVariants — the SMALL NAMED SET of ProjectionCard variants (PR-C1 §5).
 * This is exactly what Contractor B's B2 fan-out replicates: B picks a
 * variant, it does not invent a card. Seven variants, mapped to the ten
 * projections + housing:
 *
 *   1. ListSoftArchiveCard  — medications, conditions, hospitalEvents
 *   2. RegisterCard         — riskFlags, legalInstruments, coverage
 *   3. AppointmentCard      — appointments (date-by-status state machine)
 *   4. ContactCard          — contacts (flag toggles)
 *   5. ProviderCard         — providers (3-arm linked entity)
 *   6. HousingCard          — housing (0148, D9: multiple / historical
 *                             statuses; current on top, past beneath)
 *   7. FunctionalStatusCard — functionalStatus (0122; captured rows as a
 *                             compact table + a one-line uncaptured summary)
 *
 * TS-6b (2026-07-06): every variant carries a per-domain emptyLabel so a
 * sparse record shows designed empty states, never a blank card.
 *
 * FE-integration pass (2026-07-06): rows carry their LIVE edit wiring —
 * each editable card passes the spine an `edit` config (the #408 route
 * token + registry-keyed current values). Hospital events joined the
 * edit at LP-19.
 *
 * Record-add slice (founder soak feedback): every editable card carries
 * the header "+ Add" (AddRowAction → the registry-derived dialog in
 * create mode). Care providers add through their OWN dialog — joining
 * the client's care team with the Directory picker first and the
 * unlinked-by-name fallback visibly marked; provider rows carry full
 * edit + archive on their own content columns (WS-5), plus the
 * UNLINKED arm's "Link to the Directory" affordance (directory
 * care-web, 2026-07-10 — identity attaches, content never edits
 * inline). Hospital events add directly too.
 *
 * Every variant composes the shared spine (ProjectionCard + ProjectionRow):
 * provenance chip, status pill, inline-edit + archive affordances. Bodies
 * are typed per projection.
 */

import { VisitOrAppointmentDialog } from './VisitOrAppointmentDialog';
import { FUNCTIONAL_LEVEL_PILL } from '@/lib/functionalLevel';
import { byStayNewestFirst } from '@/lib/hospitalEvents';
import { CareVisitRow } from './CareVisitRow';
import { useCareVisits, visitStillAhead } from '@/lib/careVisitsApi';
import { Fragment, useId, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Camera, ChevronDown, ChevronRight, ExternalLink, ListPlus, Plus } from 'lucide-react';
import { Button } from '@/components/primitives/Button';
import { PhotoIntakeDialog } from '@/components/clientRecord/PhotoIntakeDialog';
import { MedListDialog } from '@/components/clientRecord/MedListDialog';
import { Chip } from '@/components/primitives/Chip';
import { SubListHeading } from '@/components/primitives/SubListHeading';
import { contactSentence, errorCodeOf, saveFailureText } from '@/lib/errorCopy';
import { useUndo } from '@/lib/undo';
import { useSupportContact } from '@/hooks/useSupportContact';
import { sortAllergiesBySeverity } from '@/lib/allergySort';
import { groupCareProviders, type CareProviderGroups } from '@/lib/careProviders';
import { useTenant } from '@/lib/auth';
import { canUseAiTools, canViewInvoice, canWriteClinicalRecord } from '@/lib/capabilities';
import { usePhotoIntakeDoor } from '@/lib/aiToolToggles';
import { postAllergiesNoneKnown } from '@/lib/clientRecordEditApi';
import {
  AddRowAction,
  CARD_ACCENTS,
  CardOpenContext,
  ProjectionCard,
  ProjectionRow,
  RowActions,
  FieldList,
  humanize,
  registryValuesFromRow,
  useArchivedReveal,
  usePersistedCardOpen,
  type ArchivedReveal,
  type StatusPill,
  type RowEditConfig,
} from '@/components/clientRecord/cardSpine';
import { AddProviderDialog } from '@/components/clientRecord/AddProviderDialog';
import { LinkProviderDialog } from '@/components/clientRecord/LinkProviderDialog';
import { RowEditDialog } from '@/components/clientRecord/RowEditDialog';
import { COVERAGE_PROGRAMS, FUNCTIONAL_ACTIVITIES } from '@/lib/noteVocabulary';
import { recordFieldLabel, valueLabel } from '@/lib/recordFormWords';
import { MedicationHistoryLink } from './MedicationHistory';
import { AppointmentAddNote } from './AppointmentAddNote';
import { CONTACT_ROLE_KEYS, contactRoleWord, type ContactRoleKey } from '@/lib/fieldLabels';
import {
  appointmentAwaitsNote,
  appointmentDatePassed,
  codeStatusSetLabel,
  contactEffective,
  isRecurring,
  appointmentWhen,
  nextOccurrence,
  recurrenceTagLabel,
} from '@/lib/clientRecord';
import { useToday } from '@/lib/today';
import { practiceTodayIso } from '@/lib/practiceDate';
import type {
  AllergyRow,
  AppointmentRow,
  ClientPronouns,
  ClientRecordSnapshot,
  ConditionRow,
  ContactRow,
  CoverageRow,
  FunctionalStatusRow,
  HospitalEventRow,
  LegalInstrumentRow,
  MedicationRow,
  MedicationTimeOfDay,
  ProviderRow,
  RiskFlagRow,
} from '@/lib/clientRecord';
import { proseDate, weekdayDayMonth } from '@/lib/proseDate';
import { medicationTiming, startsOn, stopsOn } from '@/lib/medicationTiming';
import { formatPhoneDisplay } from '@/lib/phone';
import { coverageCardFacts } from '@/lib/coverageCard';
import { appointmentPlaceLines, appointmentPlaceValues } from '@/lib/appointmentPlace';
import { displayValue, type DisplayScope } from '@/lib/valueCase';

/** A record date as prose ('August 27, 2026'), never a bare ISO string.
 *  Passes through undefined (an absent date renders nothing). */
function fmtDate(iso?: string): string | undefined {
  return iso ? proseDate(iso) : iso;
}

/** A stored one-line value as it reads (a first capital, and its field's
 *  abbreviations in their standard form), passing an absent value through
 *  unchanged. The stored value is never changed by display. */
function shown(v: string | null | undefined, scope: DisplayScope = 'value'): string | undefined {
  return v ? displayValue(v, scope) : undefined;
}

/** A stored phone rendered pretty ((555) 555-0123); absent stays absent. */
function fmtPhone(v?: string | null): string | undefined {
  const s = formatPhoneDisplay(v);
  return s === '' ? undefined : s;
}

/**
 * Design Pass 3 (item 5), LP-40: the row list every card renders, ACTIVE
 * rows only by default; while the page's archived line reads Hide, the
 * archived rows follow INLINE, beneath the active listings, entry order
 * (nothing is deleted; nothing lingers on screen). The card carries no
 * reveal of its own: the page's one line is the door. `renderRow` keeps
 * each variant's typed row; `activeRows` lets a card impose its own
 * active ordering (coverage's program order, the appointments partition).
 */
function archivedAwareRows<T extends { id: string; isActive: boolean }>(
  reveal: ArchivedReveal<T>,
  renderRow: (r: T) => ReactNode,
  activeRows?: readonly T[],
): ReactNode[] {
  const shown = [...(activeRows ?? reveal.active), ...(reveal.revealed ? reveal.archived : [])];
  return shown.map((r) => <Fragment key={r.id}>{renderRow(r)}</Fragment>);
}

/** Persistent light underline — links navigate (R1). */
const LINK_CLS =
  'text-sage-700 underline decoration-sage-700/30 underline-offset-2 hover:decoration-sage-700 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded-tight';

/**
 * Where a resolved provider MENTION should navigate (Design Pass 2 A8,
 * founder addition 2026-07-10: "Providers still don't have a link to the
 * directory"): a care-team row whose link arm resolved to a Directory
 * entity hops DIRECTLY to that Directory page; a pending/unlinked row
 * falls back to the People tab (whose row carries the link affordance —
 * the second hop). Never guess an identity.
 */
function mentionTarget(
  match: ProviderRow,
  tenantSlug: string,
  clientId: string,
): { to: string; kind: 'directory' | 'care-team' } {
  if (match.link.kind === 'individual') {
    return { to: `/t/${tenantSlug}/individuals/${match.link.id}`, kind: 'directory' };
  }
  if (match.link.kind === 'org') {
    return { to: `/t/${tenantSlug}/organizations/${match.link.id}`, kind: 'directory' };
  }
  return { to: `/t/${tenantSlug}/clients/${clientId}?tab=people`, kind: 'care-team' };
}

/**
 * A prescriber name that LINKS to the provider's own page (directory
 * care-web, founder feedback 2026-07-10: "all provider mentions should
 * be linked"). Resolution: the contract's prescriberId (the exact
 * client_providers row) first; the shipped name-match against active
 * care-team rows as the fallback for payload-only prescriber strings.
 * A resolved mention prefers the DIRECT Directory hop when the row's
 * link arm carries one (A8); otherwise the People tab (whose row carries
 * the Directory affordance). An unresolved name keeps the honest option:
 * the Directory search with the name prefilled (never fake an identity).
 */
export function PrescriberLink({
  name,
  prescriberId,
  providers,
}: {
  name: string;
  /** The care-team row id from the snapshot (present iff the FK resolved). */
  prescriberId?: string;
  providers: readonly ProviderRow[];
}): ReactNode {
  const { tenantSlug = '', clientId = '' } = useParams<{ tenantSlug: string; clientId: string }>();
  const match =
    (prescriberId !== undefined ? providers.find((p) => p.id === prescriberId) : undefined) ??
    // A note's old phrasing still links after the Directory renames the
    // person: the stored name is matched as well as the shown one.
    providers.find((p) => p.isActive && (p.displayName === name || p.recordedName === name));
  if (match !== undefined) {
    const target = mentionTarget(match, tenantSlug, clientId);
    return (
      <Link
        to={target.to}
        data-testid="prescriber-link"
        data-link-kind={target.kind}
        className={LINK_CLS}
      >
        {name}
      </Link>
    );
  }
  return (
    <Link
      to={`/t/${tenantSlug}/directory?q=${encodeURIComponent(name)}`}
      data-testid="prescriber-link"
      data-link-kind="search"
      className={LINK_CLS}
    >
      {name}
    </Link>
  );
}

// =====================================================================
// 1. list-soft-archive — accumulating rows, each edit + archive.
// =====================================================================

const MED_STATUS_PILL: Record<MedicationRow['status'], StatusPill> = {
  active: { label: 'Active', variant: 'affirm' },
  discontinued: { label: 'Discontinued', variant: 'neutral' },
};

/**
 * A medication's pill on `today` (LP-41, medicationTiming): one starting
 * later says when it starts, a current one with a stop date says when it
 * stops, and one stopped by its date reads as discontinued (no write).
 */
function medicationPill(r: MedicationRow, today: string): StatusPill {
  const timing = medicationTiming(r, today);
  if (timing === 'stopped') return MED_STATUS_PILL.discontinued;
  const start = startsOn(r, today);
  if (start !== null) return { label: `Starts ${weekdayDayMonth(start)}`, variant: 'neutral' };
  const stop = stopsOn(r, today);
  if (stop !== null) return { label: `Stops ${weekdayDayMonth(stop)}`, variant: 'caution' };
  return MED_STATUS_PILL.active;
}

/**
 * "Add from a photo": the camera door onto the record. One photo of a
 * med list or a page of providers is read into confirmable rows
 * (PhotoIntakeDialog); each confirmed row rides the same record-add path
 * the manual "+ Add" uses. Lives on the Medications card header, but a
 * single photo feeds BOTH the medications and the providers projections.
 *
 * Shows for a viewer who keeps the clinical record (like the manual
 * "+ Add") and uses the AI tools, while the tenant's photo tool is on
 * (usePhotoIntakeDoor: shown while that read loads or after it fails).
 */
export function AddFromPhotoAction(): ReactNode {
  const [open, setOpen] = useState(false);
  const { whoami, tenantSlug } = useTenant();
  const showDoor = usePhotoIntakeDoor(tenantSlug, canWriteClinicalRecord(whoami) && canUseAiTools(whoami));
  if (!showDoor) return null;
  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        icon={<Camera size={14} aria-hidden="true" />}
        data-testid="card-add-from-photo"
        aria-label="Add from a photo"
        onClick={() => setOpen(true)}
      >
        From a photo
      </Button>
      {open && <PhotoIntakeDialog onClose={() => setOpen(false)} />}
    </>
  );
}

/**
 * "Make med list" — build the company medication list from this client's
 * active meds, organized by time of day, ready to download as a branded .docx.
 * Deterministic + record-sourced (MedListDialog); no model touches the doses.
 * Needs the whole snapshot (client name + allergies), so it renders only when
 * the card is given the record. It shows only to a viewer who keeps the
 * record (canWriteClinicalRecord): a SPICE-only therapist reads the
 * medications but makes no list from them. The Writing page's
 * Medication List entry hides on the same predicate.
 */
export function MakeMedListAction({ record }: { record: ClientRecordSnapshot }): ReactNode {
  const [open, setOpen] = useState(false);
  const canWrite = canWriteClinicalRecord(useTenant().whoami);
  if (!canWrite) return null;
  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        icon={<ListPlus size={14} aria-hidden="true" />}
        data-testid="card-make-med-list"
        aria-label="Make a medication list"
        onClick={() => setOpen(true)}
      >
        Make med list
      </Button>
      {open && <MedListDialog record={record} onClose={() => setOpen(false)} />}
    </>
  );
}

export function MedicationsCard({
  rows,
  providers = [],
  record,
}: {
  rows: MedicationRow[];
  /** Care-providers projection — resolves prescriber names to Directory
   *  links (record-structure build; the Care record passes it). */
  providers?: readonly ProviderRow[];
  /** The whole snapshot — enables the "Make med list" action (needs the
   *  client name + allergies). Absent in isolated card tests; the action
   *  simply does not render then. */
  record?: ClientRecordSnapshot;
}): ReactNode {
  const reveal = useArchivedReveal(rows);
  const headingId = useId();
  // The practice's day (LP-41): the page's, else the one clock.
  const today = useToday() ?? practiceTodayIso();
  const renderRow = (r: MedicationRow, repeat = false): ReactNode => (
    <ProjectionRow
      envelope={r}
      title={displayValue(r.name, 'medication')}
      pill={r.isActive ? medicationPill(r, today) : MED_STATUS_PILL[r.status]}
      repeat={repeat}
      edit={{
        projection: 'medications',
        // The linked care-team row opens as the prescriber's pick.
        values: {
          ...registryValuesFromRow({ ...r }, 'medications'),
          ...(r.prescriberId ? { prescriber_provider_id: r.prescriberId } : {}),
        },
      }}
    >
      <FieldList
        fields={[
          { label: recordFieldLabel('medications', 'dose'), value: shown(r.dose, 'medication') },
          {
            label: recordFieldLabel('medications', 'times_of_day'),
            value: (r.timesOfDay ?? []).map((t) => valueLabel('medications', 'times_of_day', t)).join(', '),
          },
          { label: recordFieldLabel('medications', 'schedule'), value: shown(r.schedule, 'medication') },
          { label: recordFieldLabel('medications', 'started_on'), value: fmtDate(r.startedOn) },
          { label: recordFieldLabel('medications', 'ended_on'), value: fmtDate(r.endedOn) },
          {
            label: recordFieldLabel('medications', 'prescriber'),
            value: r.prescriber ? (
              <PrescriberLink name={r.prescriber} prescriberId={r.prescriberId} providers={providers} />
            ) : undefined,
          },
          { label: recordFieldLabel('medications', 'reason'), value: shown(r.reason, 'medication_reason') },
        ]}
      />
      {!repeat && (
        <div className="mt-1">
          <MedicationHistoryLink rowId={r.id} name={r.name} />
        </div>
      )}
    </ProjectionRow>
  );
  const groups = medicationGroups(reveal.active, today);
  const grouped =
    groups === null ? null : subListGroups('medications', headingId, groups, (a) => renderRow(a.row, a.repeat));
  return (
    <ProjectionCard
      archivedShown={reveal.showing}
      title="Medications"
      testId="card-medications"
      // Jump-strip anchor (care-record restructure): the hash target the
      // at-a-glance pill emits; the G4 hash machinery scrolls + force-opens.
      id="medications"
      // PART 4 (domain color system): amber — matches the "Med & allergy"
      // Latest-activity tag.
      accent={CARD_ACCENTS.medications}
      emptyLabel="Add the first medication, or take a photo of the list. You'll also see medications here once notes confirm them."
      // A1: one of the Care tab's two budgeted heavy adds (the anchor
      // sections a CM adds to most); every other card takes the quiet add.
      // The camera door + the "Make med list" door sit beside the manual add.
      action={
        <span className="flex items-center gap-1">
          {/* Nothing to list on an empty card. */}
          {record && rows.length > 0 && <MakeMedListAction record={record} />}
          <AddFromPhotoAction />
          <AddRowAction projection="medications" emphasis="primary" />
        </span>
      }
    >
      {grouped === null
        ? archivedAwareRows(reveal, (r) => renderRow(r))
        : [...grouped, ...archivedAwareRows(reveal, (r) => renderRow(r), [])]}
    </ProjectionCard>
  );
}

/** One sub-list of a card: its key (the testid part), its heading, and
 *  its rows in the order they read. */
interface SubListGroup<T> {
  key: string;
  heading: string;
  rows: readonly T[];
}

/**
 * A card's rows in sub-lists: each group with rows is a section labelled
 * by its SubListHeading, and a group with no rows has neither. Testids are
 * `${prefix}-group-${key}` on the section and `…-heading` on the heading;
 * `headingId` (a useId) keeps the heading ids unique on the page. The
 * caller adds any archived rows beneath.
 */
function subListGroups<T extends { id: string }>(
  prefix: string,
  headingId: string,
  groups: readonly SubListGroup<T>[],
  renderRow: (r: T) => ReactNode,
): ReactNode[] {
  return groups
    .filter((g) => g.rows.length > 0)
    .map((g) => (
      <li key={`group-${g.key}`} className="list-none">
        <section
          data-testid={`${prefix}-group-${g.key}`}
          aria-labelledby={`${headingId}-${g.key}`}
          className="flex flex-col gap-2"
        >
          <SubListHeading id={`${headingId}-${g.key}`} testId={`${prefix}-group-${g.key}-heading`}>
            {g.heading}
          </SubListHeading>
          <ul className="flex flex-col gap-2">
            {g.rows.map((r) => (
              <Fragment key={r.id}>{renderRow(r)}</Fragment>
            ))}
          </ul>
        </section>
      </li>
    ));
}

type MedicationGroupKey = MedicationTimeOfDay | 'none' | 'current' | 'starting_soon' | 'discontinued';

/** The Medications card's groups: the five times of day, in the
 *  medication list's order, then No time set (or, when nothing current
 *  has a When, one Current group), then Starting soon, then
 *  Discontinued. */
const TIME_GROUPS: readonly { key: MedicationGroupKey; heading: string }[] = [
  ...(['morning', 'afternoon', 'evening', 'bedtime', 'as_needed'] as const).map((key) => ({
    key,
    heading: valueLabel('medications', 'times_of_day', key),
  })),
  { key: 'none', heading: 'No time set' },
];
const CURRENT_GROUP = { key: 'current', heading: 'Current' } as const;
const STARTING_SOON_GROUP = { key: 'starting_soon', heading: 'Starting soon' } as const;
const DISCONTINUED_GROUP = { key: 'discontinued', heading: 'Discontinued' } as const;

/** One appearance of a medication in a group: the row, or a later,
 *  display-only repeat of it. Keyed `${block}-${id}`. */
interface MedicationAppearance {
  id: string;
  row: MedicationRow;
  repeat: boolean;
}

/**
 * The card's non-archived rows (in the read's order) grouped on `today`
 * (the practice day) by medicationTiming (LP-41), or null when the card
 * stays one flat list: it groups once a current medication has a When or
 * a medication starts on a later day, so a card with neither reads as it
 * always has (a medication stopped by its date reads Discontinued there).
 * Grouped, the current medications come first: by When when any has one,
 * each under every block it is ticked for (its first appearance, in block
 * order, is the row, and each later one a display-only repeat) and those
 * with no When under No time set; otherwise all under Current. Then
 * Starting soon, soonest start first, whatever the ticks; then
 * Discontinued, a discontinued medication or one stopped by its date. The
 * directions never place a row. The shared renderer draws only the groups
 * with rows.
 */
function medicationGroups(rows: readonly MedicationRow[], today: string): SubListGroup<MedicationAppearance>[] | null {
  const live = rows.filter((r) => r.isActive);
  const current = live.filter((r) => medicationTiming(r, today) === 'current');
  const starting = live
    .filter((r) => medicationTiming(r, today) === 'starting_soon')
    .map((r, i) => ({ r, i, on: startsOn(r, today) ?? '' }))
    .sort((a, b) => a.on.localeCompare(b.on) || a.i - b.i)
    .map((x) => x.r);
  const stopped = live.filter((r) => medicationTiming(r, today) === 'stopped');
  const byTime = current.some((r) => (r.timesOfDay ?? []).length > 0);
  if (!byTime && starting.length === 0) return null;
  const seen = new Set<string>();
  const appear = (key: MedicationGroupKey, members: readonly MedicationRow[]): MedicationAppearance[] =>
    members.map((row) => {
      const repeat = seen.has(row.id);
      seen.add(row.id);
      return { id: `${key}-${row.id}`, row, repeat };
    });
  const currentGroups = byTime
    ? TIME_GROUPS.map((g) => ({
        key: g.key,
        heading: g.heading,
        rows: appear(
          g.key,
          g.key === 'none'
            ? current.filter((r) => (r.timesOfDay ?? []).length === 0)
            : current.filter((r) => (r.timesOfDay ?? []).includes(g.key as MedicationTimeOfDay)),
        ),
      }))
    : [{ ...CURRENT_GROUP, rows: appear(CURRENT_GROUP.key, current) }];
  return [
    ...currentGroups,
    { ...STARTING_SOON_GROUP, rows: appear(STARTING_SOON_GROUP.key, starting) },
    { ...DISCONTINUED_GROUP, rows: appear(DISCONTINUED_GROUP.key, stopped) },
  ];
}

const CONDITION_STATUS_PILL: Record<ConditionRow['status'], StatusPill> = {
  active: { label: 'Active', variant: 'affirm' },
  watch: { label: 'Watch', variant: 'neutral' },
  resolved: { label: 'Resolved', variant: 'neutral' },
};

/**
 * The order inside each Conditions group (LP-19, Amanda: "if they have
 * the dates, then it should be by date, newest first, but if there are no
 * dates, it should be alphabetical... put the dated ones first, the
 * undated ones second"). A row is dated when it shows a date: its
 * `notedOn`. A row confirmed from an assessment carries the note's
 * provenance but no `notedOn`, so it is undated here, and an assessment's
 * conditions read alphabetically rather than in the order they were
 * written. Dated rows sort newest first; ties among them fall back to
 * reversed wire order (the array is reversed before the stable sort, so
 * a same-transaction batch's later-written row wins the tie). Undated
 * rows follow, by name, case-insensitive.
 */
function sortConditions(rows: readonly ConditionRow[]): ConditionRow[] {
  return [...rows].reverse().sort((a, b) => {
    const aDate = a.notedOn;
    const bDate = b.notedOn;
    if (aDate !== undefined && bDate !== undefined) return aDate < bDate ? 1 : aDate > bDate ? -1 : 0;
    if (aDate !== undefined) return -1;
    if (bDate !== undefined) return 1;
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  });
}

/** The Conditions card's groups, in the order they read. */
const CONDITION_GROUPS: readonly { key: ConditionRow['status']; heading: string }[] = [
  { key: 'active', heading: 'Active' },
  { key: 'watch', heading: 'Watch' },
  { key: 'resolved', heading: 'Resolved' },
];

export function ConditionsCard({ rows }: { rows: ConditionRow[] }): ReactNode {
  const reveal = useArchivedReveal(rows);
  const sortedActive = sortConditions(reveal.active);
  const headingId = useId();
  const renderRow = (r: ConditionRow): ReactNode => (
    <ProjectionRow
      envelope={r}
      title={displayValue(r.name, 'diagnosis')}
      pill={CONDITION_STATUS_PILL[r.status]}
      edit={{
        projection: 'conditions',
        values: registryValuesFromRow({ ...r }, 'conditions'),
      }}
    >
      <FieldList
        fields={[
          { label: recordFieldLabel('conditions', 'noted_on'), value: fmtDate(r.notedOn) },
          { label: recordFieldLabel('conditions', 'note'), value: r.note },
        ]}
      />
    </ProjectionRow>
  );
  // The active rows by status, Active, then Watch, then Resolved, each
  // dated newest first, then undated by name; a status with no rows has
  // no heading. Archived rows
  // show with the page's archived line: beneath every group, in entry order, under no
  // heading. Each row keeps its pill, the colour cue.
  const grouped = subListGroups(
    'conditions',
    headingId,
    CONDITION_GROUPS.map((g) => ({ ...g, rows: sortedActive.filter((r) => r.status === g.key) })),
    renderRow,
  );
  return (
    <ProjectionCard
      archivedShown={reveal.showing}
      title="Conditions"
      testId="card-conditions"
      id="conditions"
      // Opens by default — a first-visit collapse would hide
      // safety-relevant conditions behind a click. collapsedCount still
      // names the count once the CM has manually collapsed it (the
      // persisted per-card choice). Empty, the card keeps its heading
      // and reads as no entries (LP-29).
      collapsedCount={reveal.active.length}
      // The heading takes the palette's structural forest tone rather
      // than the memory family's heading hue; the band keeps the
      // Health-category tint it shares with Hospital & rehab and
      // Functional status.
      accent={{ band: CARD_ACCENTS.memory.band, heading: 'text-sage-800' }}
      emptyLabel="Add the first condition. Once a note confirms a condition, you'll also see it here."
      action={<AddRowAction projection="conditions" />}
    >
      {[...grouped, ...archivedAwareRows(reveal, renderRow, [])]}
    </ProjectionCard>
  );
}

export function HospitalEventsCard({ rows }: { rows: HospitalEventRow[] }): ReactNode {
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();
  // Newest first, undated stays after (LP-19), the server's order kept.
  const reveal = useArchivedReveal([...rows].sort(byStayNewestFirst));
  return (
    <ProjectionCard
      archivedShown={reveal.showing}
      title="Hospital & rehab events"
      testId="card-hospital-events"
      id="hospital-events"
      // Opens like every card; collapsed, the header names the ACTIVE
      // count, and a person's stored open or closed choice wins
      // (archived existence rides the page's archived line).
      collapsedCount={reveal.active.length}
      // PART 4: heather (memory) — matches the "Health" Latest-activity tag.
      accent={CARD_ACCENTS.memory}
      emptyLabel="Add a hospital or rehab stay. Once a note records a hospital or rehab stay, you'll see it here."
      // A direct ADD through the same header affordance and
      // registry-derived create dialog as every editable card; the add
      // may link the facility to a Directory organization. Each row edits
      // through the same dialog (LP-19), the facility's link included,
      // and archives and restores like every record row.
      action={
        <AddRowAction projection="hospital-events" />
      }
    >
      {archivedAwareRows(reveal, (r) => (
        // A linked stay's place is a link to its Directory page.
        <ProjectionRow
          envelope={r}
          title={valueLabel('hospital-events', 'kind', r.kind)}
          pill={{ label: (r.eventOn ? proseDate(r.eventOn) : shown(r.dateText)) ?? 'date unknown', variant: 'neutral' }}
          edit={{ projection: 'hospital-events', values: registryValuesFromRow({ ...r }, 'hospital-events') }}
        >
          <FieldList
            fields={[
              {
                label: recordFieldLabel('hospital-events', 'facility'),
                value:
                  r.facilityOrgId === undefined ? (
                    r.facility
                  ) : (
                    <Link
                      to={`/t/${tenantSlug}/organizations/${r.facilityOrgId}`}
                      data-testid="hospital-facility-link"
                      className="font-medium text-sage-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded-tight"
                    >
                      {r.facility}
                    </Link>
                  ),
              },
              { label: recordFieldLabel('hospital-events', 'note'), value: r.note },
            ]}
          />
        </ProjectionRow>
      ))}
    </ProjectionCard>
  );
}

// =====================================================================
// 1b. allergies: the patient-safety card, pinned at the TOP of the
//     record with the clinical-risk accent. Deliberately NOT the
//     collapse-when-empty shell: an empty allergy list is itself safety
//     information, so the card stays full-size. Three states: "No
//     allergies recorded" (not asked: the attention register, with the
//     quiet "Record no known allergies" affordance); "No known
//     allergies, confirmed <date>" (asked and answered none: the calm
//     register, never the red accent), with "Add an allergy" on its own
//     line under it; and the red-accent list. Recording none gives the
//     house Undo. There is no separate removal: the server clears the
//     marker when an allergy is saved, and refuses the marker while
//     active rows exist (409).
// =====================================================================

const ALLERGY_SEVERITY_PILL: Record<NonNullable<AllergyRow['severity']>, StatusPill> = {
  severe: { label: 'Severe', variant: 'risk' },
  moderate: { label: 'Moderate', variant: 'caution' },
  mild: { label: 'Mild', variant: 'neutral' },
  unknown: { label: 'Severity unknown', variant: 'neutral' },
};

export function AllergiesCard({
  rows,
  noneKnownOn = null,
}: {
  rows: AllergyRow[];
  /** The NKDA confirmation date (0137 §5); null = question unanswered. */
  noneKnownOn?: string | null;
}): ReactNode {
  const { tenantSlug = '', clientId = '' } = useParams<{ tenantSlug: string; clientId: string }>();
  const queryClient = useQueryClient();
  const { push: pushUndo } = useUndo();
  // The error copy's contact reads the shared tenant-config cache the app
  // shell fills; the card makes no call of its own for it.
  const contact = useSupportContact({ enabled: false });
  const [nkdaError, setNkdaError] = useState<string | null>(null);
  const nkdaMut = useMutation({
    mutationFn: (noneKnown: boolean) => postAllergiesNoneKnown(tenantSlug, clientId, noneKnown),
    onSuccess: async (_result, noneKnown) => {
      setNkdaError(null);
      // ONE audited re-read of the aggregate (the RowEditDialog posture).
      await queryClient.invalidateQueries({ queryKey: ['clientRecord'] });
      if (!noneKnown) return;
      // Recording none gives the house Undo. The undo posts the marker
      // back and catches its own failure, since the toast surfaces none;
      // an allergy saved meanwhile has already cleared the marker, and
      // the post is then a no-op on the server.
      pushUndo({
        label: 'No known allergies recorded',
        undo: async () => {
          try {
            await postAllergiesNoneKnown(tenantSlug, clientId, false);
            await queryClient.invalidateQueries({ queryKey: ['clientRecord'] });
          } catch (err) {
            setNkdaError(
              saveFailureText(err, contact) ??
                contactSentence("That didn't undo. Try again, and if it happens again, tell {contact_system}.", contact),
            );
          }
        },
      });
    },
    onError: (err) => {
      setNkdaError(
        errorCodeOf(err) === 'allergies_on_file'
          ? 'There are allergies on file. Remove them first if none are truly known.'
          : (saveFailureText(err, contact) ??
              contactSentence("That didn't save. Try again, and if it happens again, tell {contact_system}.", contact)),
      );
    },
  });

  // Item 5: archived allergies leave the screen; the safety states key
  // off ACTIVE rows (the server's NKDA exclusion is against active rows).
  const reveal = useArchivedReveal(rows);
  const confirmedNone = reveal.active.length === 0 && noneKnownOn !== null;
  // Care-record restructure: the safety card learns the ONE shared
  // collapse (usePersistedCardOpen — the ProjectionCard machinery, never
  // a local copy; the 'allergies' id keeps the jump-strip hash force-open
  // working). SAFETY-PRESERVING: collapse only exists while active rows
  // do, the collapsed state keeps an always-visible one-line summary
  // naming EVERY substance with its severity, and the empty / confirmed
  // no-known-allergies one-liners are unaffected. Default expanded.
  const { open, toggle, openCard } = usePersistedCardOpen('card-allergies', 'allergies', false, reveal.showing);
  const bodyId = useId();
  // "Record no known allergies" is a clinical write
  // (postAllergiesNoneKnown), hidden for a viewer who does not keep the
  // clinical record; "Add an allergy" hides through AddRowAction's own
  // gate. The confirmed / empty / listed safety STATES still read; only
  // the write doors go.
  const canWrite = canWriteClinicalRecord(useTenant().whoami);
  const hasActive = reveal.active.length > 0;
  const listOpen = !hasActive || open;
  // Most severe first, then alphabetical — the ONE order every allergy
  // list on the record shares (src/lib/allergySort.ts). The revealed
  // ARCHIVED block keeps reveal.archived's own wire/entry order
  // unchanged (item 5's ruled history order); only the active rows sort.
  const sortedActive = sortAllergiesBySeverity(reveal.active);
  return (
    <section
      id="allergies"
      data-testid="card-allergies"
      data-empty={reveal.active.length === 0 ? 'true' : 'false'}
      className={[
        // scroll-mt: jump-strip landing room (block:'start' + the sticky
        // header/strip) — same clearance as ProjectionCard's anchors.
        'rounded-card bg-paper shadow-sh1 border border-sage-800/[8%] p-4 scroll-mt-[100px] md:scroll-mt-[110px]',
        // PART 4 (UI polish pass, founder: "severe can be red inline, but
        // the section doesn't have to be red"): the not-asked + listed
        // states SOFTEN from the old solid-pepper accent to a calmer warm
        // terracotta (a half-strength pepper-soft band) — clearly quieter
        // than the Risk-flags card's pepper-soft band. A CONFIRMED "none
        // known" stays the calm sage (never an alert). The band NEVER
        // collapses away — severity reads at a glance even on the folded
        // card; the inline (severe) marker below KEEPS the strong
        // pepper-deep red regardless of this softened section accent.
        confirmedNone ? 'border-l-4 border-l-sage-400' : 'border-l-4 border-l-pepper-soft/50',
      ].join(' ')}
    >
      <header className={`flex items-center gap-2 ${listOpen ? 'mb-3' : 'mb-1.5'}`}>
        {/* The shared disclosure chevron (G5 anatomy) — only when there
            are rows to fold; the safety empty states never collapse. */}
        {hasActive && (
          <Button
            variant="ghost"
            size="sm"
            className="!min-h-0 -ml-1 !px-1 !py-0.5"
            data-testid="section-collapse"
            aria-expanded={open}
            aria-controls={open ? bodyId : undefined}
            aria-label={open ? 'Collapse Allergies' : 'Expand Allergies'}
            icon={
              open ? (
                <ChevronDown size={16} aria-hidden="true" />
              ) : (
                <ChevronRight size={16} aria-hidden="true" />
              )
            }
            onClick={toggle}
          />
        )}
        {/* Design Pass 2 (A2, one heading system): the letterspaced-caps
            treatment retired — Allergies joins the record's ONE heading
            scale (display serif, title case, text-xl). The safety register
            stays in the ACCENT: the clinical-warn title color + the pepper
            left border, never a second type system. */}
        <h3
          className={[
            'font-display text-xl',
            // The clinical-warn burgundy, not pepper — pepper is the SPICE
            // accent, and this card renders on the care-management face too.
            confirmedNone ? 'text-sage-800' : 'text-clinical-warn',
          ].join(' ')}
        >
          Allergies
        </h3>
        <span className="ml-auto shrink-0">
          {/* Open-on-add: adding to a collapsed safety card reveals the
              full rows so the new allergy is immediately visible. */}
          <CardOpenContext.Provider value={openCard}>
            <AddRowAction projection="allergies" />
          </CardOpenContext.Provider>
        </span>
      </header>
      {/* COLLAPSED: existence + severity are NEVER hidden — the one-line
          summary names every active substance; severe ones keep the
          risk-register (pepper) marker so severity reads at a glance. */}
      {hasActive && !open && (
        <p data-testid="allergies-collapsed-summary" className="text-sm text-sage-800">
          {sortedActive.map((r, i) => (
            <Fragment key={r.id}>
              {i > 0 && <span aria-hidden="true"> · </span>}
              <span className="font-medium">{displayValue(r.substance, 'allergy')}</span>
              {r.severity === 'severe' ? (
                <span
                  data-testid="allergy-severe-marker"
                  className="font-semibold text-clinical-warn"
                >
                  {' '}
                  (severe)
                </span>
              ) : r.severity && r.severity !== 'unknown' ? (
                <span className="text-ink-soft"> ({r.severity})</span>
              ) : null}
            </Fragment>
          ))}
        </p>
      )}
      {reveal.active.length === 0 ? (
        confirmedNone ? (
          <div data-testid="allergies-none-known" className="text-sm text-sage-800">
            <p>No known allergies, confirmed {proseDate(noneKnownOn!)}.</p>
            {/* Adding an allergy is what ends the confirmed state (the
                server clears the marker on the save). The door sits on
                its own line under the sentence and opens the card so the
                new row shows. */}
            <div className="mt-1">
              <CardOpenContext.Provider value={openCard}>
                <AddRowAction projection="allergies" text="Add an allergy" testId="allergies-add-an-allergy" />
              </CardOpenContext.Provider>
            </div>
          </div>
        ) : (
          <div className="text-sm">
            <p data-testid="allergies-empty" className="italic text-ink-soft">
              Record each allergy. Mark 'no known allergies' only once the client confirms there are none.
            </p>
            {/* The quiet NKDA affordance (0137 §5): asked-and-answered-none
                is a recordable fact now, distinct from not-asked. */}
            {canWrite && (
              <Button
                variant="ghost"
                size="sm"
                className="mt-1"
                data-testid="allergies-none-known-set"
                loading={nkdaMut.isPending}
                onClick={() => nkdaMut.mutate(true)}
              >
                Record no known allergies
              </Button>
            )}
          </div>
        )
      ) : null}
      {/* Item 5: ONE row list — active rows lead; archived allergies sit
          behind the page's archived line, beneath them (an all-archived list
          shows under the safety states above while the line reads Hide). Folds
          under the collapse (the summary above keeps every substance +
          severity visible); the safety states never fold. */}
      {listOpen && (reveal.active.length > 0 || reveal.showing) && (
        <ul
          id={bodyId}
          className={`flex flex-col gap-2 ${reveal.active.length === 0 ? 'mt-2' : ''}`}
        >
          {archivedAwareRows(
            reveal,
            (r) => (
              <ProjectionRow
                envelope={r}
                title={displayValue(r.substance, 'allergy')}
                pill={r.severity ? ALLERGY_SEVERITY_PILL[r.severity] : null}
                edit={{
                  projection: 'allergies',
                  values: registryValuesFromRow({ ...r }, 'allergies'),
                }}
              >
                <FieldList fields={[{ label: 'Reaction', value: shown(r.reaction) }]} />
              </ProjectionRow>
            ),
            sortedActive,
          )}
        </ul>
      )}
      {nkdaError !== null && (
        <p role="alert" data-testid="allergies-none-known-error" className="mt-2 text-xs text-pepper">
          {nkdaError}
        </p>
      )}
    </section>
  );
}

// =====================================================================
// 2. register-upsert-by-key — one row per natural key.
// =====================================================================

const RISK_LEVEL_PILL: Record<RiskFlagRow['level'], StatusPill> = {
  elevated: { label: 'Elevated', variant: 'risk' },
  watch: { label: 'Watch', variant: 'neutral' },
  resolved: { label: 'Resolved', variant: 'neutral' },
};

export function RiskFlagsCard({ rows }: { rows: RiskFlagRow[] }): ReactNode {
  const reveal = useArchivedReveal(rows);
  return (
    <ProjectionCard
      archivedShown={reveal.showing}
      title="Risk flags"
      testId="card-risk-flags"
      // G4 (Soak #2): the scroll anchor for the top-bar risk line and the
      // AttentionBand jump-to-risk (#risk-flags), consumed by useHashScroll.
      id="risk-flags"
      // PART 4: pepper — the risk register; stays the strongest accent.
      accent={CARD_ACCENTS.risk}
      emptyLabel="All clear: no risk flags."
      action={<AddRowAction projection="risk-flags" />}
    >
      {archivedAwareRows(reveal, (r) => (
        <ProjectionRow
          envelope={r}
          title={valueLabel('risk-flags', 'category', r.category)}
          pill={RISK_LEVEL_PILL[r.level]}
          edit={{
            projection: 'risk-flags',
            values: registryValuesFromRow({ ...r }, 'risk-flags'),
          }}
        >
          <FieldList fields={[{ label: recordFieldLabel('risk-flags', 'basis'), value: r.basis }]} />
        </ProjectionRow>
      ))}
    </ProjectionCard>
  );
}

const LEGAL_STATUS_PILL: Record<LegalInstrumentRow['status'], StatusPill> = {
  on_file: { label: 'On file', variant: 'affirm' },
  in_progress: { label: 'In progress', variant: 'neutral' },
  recommended: { label: 'Recommended', variant: 'neutral' },
  missing: { label: 'Missing', variant: 'neutral' },
  unknown: { label: 'Unknown', variant: 'neutral' },
};

export function LegalInstrumentsCard({ rows }: { rows: LegalInstrumentRow[] }): ReactNode {
  const { tenantSlug = '', clientId = '' } = useParams<{ tenantSlug: string; clientId: string }>();
  // Attaching the actual document is a Documents-tab write, so the quiet
  // per-row door below rides the same clinical-record write gate as the
  // card's own "+ Add".
  const canWrite = canWriteClinicalRecord(useTenant().whoami);
  const reveal = useArchivedReveal(rows);
  return (
    <ProjectionCard
      archivedShown={reveal.showing}
      title="Legal & wishes"
      testId="card-legal-instruments"
      // PART 4: quiet sage (logistics) — one family for the whole
      // Life & logistics tab.
      accent={CARD_ACCENTS.logistics}
      emptyLabel="No legal instruments on file yet (POA, HCP, code status)."
      action={<AddRowAction projection="legal-instruments" />}
    >
      {archivedAwareRows(reveal, (r) => (
        <ProjectionRow
          envelope={r}
          title={humanize(r.instrument)}
          pill={LEGAL_STATUS_PILL[r.status]}
          edit={{
            projection: 'legal-instruments',
            values: registryValuesFromRow({ ...r }, 'legal-instruments'),
          }}
        >
          <div className="space-y-1">
            <FieldList
              fields={[
                // codeStatusValues present iff instrument ∈ {code_status, polst}
                // (0136 — the SET: "DNR + DNI + DNH"). ALL-CAPS acronyms +
                // plain words via the shared label map (DL v1 polish);
                // ['unknown'] is a recorded vocab value → honest "Not recorded".
                {
                  label: recordFieldLabel('legal-instruments', 'code_status_values'),
                  value: r.codeStatusValues ? codeStatusSetLabel(r.codeStatusValues) : undefined,
                },
                { label: recordFieldLabel('legal-instruments', 'designee'), value: r.designee },
                { label: recordFieldLabel('legal-instruments', 'effective_on'), value: fmtDate(r.effectiveOn) },
              ]}
            />
            {/* "On file" names a status, not a stored file — this door
                carries the instrument along so Documents can preselect
                its "File as" kind, the one click a status can't itself
                supply. */}
            {canWrite && (
              <Link
                to={`/t/${tenantSlug}/clients/${clientId}?tab=documents&kind=${r.instrument}`}
                data-testid={`legal-attach-document-${r.id}`}
                className="inline-flex text-xs font-medium text-sage-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded-tight"
              >
                Attach the document
              </Link>
            )}
          </div>
        </ProjectionRow>
      ))}
    </ProjectionCard>
  );
}

const COVERAGE_STATUS_PILL: Record<NonNullable<CoverageRow['status']>, StatusPill> = {
  active: { label: 'Active', variant: 'affirm' },
  inactive: { label: 'Inactive', variant: 'neutral' },
  pending: { label: 'Pending', variant: 'neutral' },
  unverified: { label: 'Unverified', variant: 'neutral' },
};

export function CoverageCard({ rows }: { rows: CoverageRow[] }): ReactNode {
  const reveal = useArchivedReveal(rows);
  // ACTIVE coverage itemizes in PROGRAM order (record-structure ruling:
  // Medicare A, then B, then D — the ONE registry's canonical order),
  // never the wire/creation order. The revealed archived block keeps the
  // ruled entry order (item 5).
  const sortedActive = [...reveal.active].sort(
    (a, b) => COVERAGE_PROGRAMS.indexOf(a.program) - COVERAGE_PROGRAMS.indexOf(b.program),
  );
  return (
    // "Insurance" on every user-facing surface (WS-8 rename, founder
    // 2026-07-10); the coverage wire/registry tokens (projection route,
    // testId, coverage_change) deliberately DO NOT change.
    <ProjectionCard
      archivedShown={reveal.showing}
      title="Insurance"
      testId="card-coverage"
      // PART 4: quiet sage (logistics) — one family for the whole
      // Life & logistics tab.
      accent={CARD_ACCENTS.logistics}
      emptyLabel="No insurance recorded yet (Medicare, Medicaid, LTC)."
      action={<AddRowAction projection="coverage" />}
    >
      {archivedAwareRows(reveal, (r) => (
        <ProjectionRow
          envelope={r}
          title={humanize(r.program)}
          pill={r.status ? COVERAGE_STATUS_PILL[r.status] : null}
          edit={{
            projection: 'coverage',
            values: registryValuesFromRow({ ...r }, 'coverage'),
          }}
        >
          <FieldList
            fields={[
              // Program + its Status render on the title line above (title =
              // program, pill = status, the house pattern). Below the title:
              // the insurance company (directly under the program, founder's
              // Soak #2 ask), the card's fields in the form's order, then
              // Verified and Notes.
              { label: recordFieldLabel('coverage', 'provider'), value: r.provider },
              ...coverageCardFacts(r),
              { label: 'Verified', value: fmtDate(r.verifiedOn) },
              { label: recordFieldLabel('coverage', 'detail'), value: r.detail },
            ]}
          />
        </ProjectionRow>
      ), sortedActive)}
    </ProjectionCard>
  );
}

// =====================================================================
// 3. appointment — date-by-status state machine.
// =====================================================================

const APPT_STATUS_PILL: Record<AppointmentRow['status'], StatusPill> = {
  scheduled: { label: 'Scheduled', variant: 'neutral' },
  attended: { label: 'Attended', variant: 'affirm' },
  missed: { label: 'Missed', variant: 'neutral' },
  cancelled: { label: 'Cancelled', variant: 'neutral' },
};

/** B1b: a scheduled appointment whose date has passed needs attention —
 *  derived at render time (no timers exist). The pill renders NEXT TO
 *  the provider's name (the row title), so the words must be about the
 *  appointment, never readable as being about the person: "Date passed"
 *  read as a deceased marker (WS-5 team feedback 2026-07-10, "there is
 *  a bubble that says 'Date Passed' next to a doctor's name"). */
const APPT_DATE_PASSED_PILL: StatusPill = { label: 'Past appointment', variant: 'caution' };

/** The date field shown depends on the status (the state machine). ISO dates
 *  render as prose ('August 27, 2026'); dateText is already the note's own
 *  phrasing. A scheduled recurring series shows its NEXT occurrence (0144),
 *  never its stale anchor. */
function appointmentDate(r: AppointmentRow, today: string): string {
  if (r.status === 'scheduled') {
    // nextOccurrence is scheduledFor for a one-off, the next occurrence for
    // a recurring series, undefined for a dateText-only row.
    return appointmentWhen(r, today) ?? shown(r.dateText) ?? 'date TBD';
  }
  if (r.status === 'attended' || r.status === 'missed') {
    return fmtDate(r.occurredOn) ?? shown(r.dateText) ?? 'date unknown';
  }
  // cancelled
  return shown(r.dateText) ?? fmtDate(r.scheduledFor) ?? 'was unscheduled';
}

/**
 * The appointment's provider mention — a LINK to the client's care team
 * (People tab) when the row's provider resolves to a care-team row
 * (directory care-web, founder feedback 2026-07-10). Resolution mirrors
 * PrescriberLink exactly: the contract's providerId (the [M2] FK) first,
 * then the exact-name match against ACTIVE care-team rows (the shipped
 * resolution for payload-only strings — no flow populates the FK yet).
 * A name matching no care-team row stays plain text: no identity, no
 * link (never guess).
 */
function AppointmentProviderTitle({
  row,
  providers,
}: {
  row: AppointmentRow;
  providers: readonly ProviderRow[];
}): ReactNode {
  const { tenantSlug = '', clientId = '' } = useParams<{ tenantSlug: string; clientId: string }>();
  const match =
    (row.providerId !== undefined
      ? providers.find((p) => p.id === row.providerId)
      : undefined) ?? providers.find((p) => p.isActive && p.displayName === row.provider);
  if (match === undefined) return row.provider;
  // A8: prefer the DIRECT Directory hop when the care-team row is linked;
  // People (which carries the link affordance) is the fallback.
  const target = mentionTarget(match, tenantSlug, clientId);
  return (
    <Link
      to={target.to}
      data-testid="appointment-provider-link"
      data-link-kind={target.kind}
      className={LINK_CLS}
    >
      {row.provider}
    </Link>
  );
}

/**
 * Item 5 partition (founder: "Past appointments not archived should also
 * not be on the care record screen"): among ACTIVE rows, the card
 * default-shows the UPCOMING appointments (scheduled, date not passed —
 * an undated scheduled row counts as upcoming; it cannot be past) plus
 * the SINGLE most recent past visit for context. Older past rows
 * collapse behind "Past appointments (N) · Show" — appointment-date
 * order, newest first, undated last. Archived rows answer to the
 * page's one archived line ("Past" is history; "Archived" is
 * removed-from-record — two distinct doors).
 */
/** A live scheduled row counts as UPCOMING when it is a recurring series
 *  (it repeats — a passed anchor never makes it stale) OR its date has not
 *  passed. Shared by the partition AND the pill so the card agrees with
 *  every other surface (0144). */
function isUpcomingAppointment(r: AppointmentRow, today: string): boolean {
  return r.status === 'scheduled' && (isRecurring(r) || !appointmentDatePassed(r, today));
}

export function partitionAppointments(
  active: readonly AppointmentRow[],
  today: string,
): { upcoming: AppointmentRow[]; recentPast: AppointmentRow | null; olderPast: AppointmentRow[] } {
  const upcoming = active.filter((r) => isUpcomingAppointment(r, today));
  // Soonest EFFECTIVE date first (a recurring series by its NEXT occurrence,
  // never its stale anchor); undated (date TBD) after the dated ones.
  upcoming.sort((a, b) =>
    (nextOccurrence(a, today) ?? '9999').localeCompare(nextOccurrence(b, today) ?? '9999'),
  );
  const pastDate = (r: AppointmentRow): string => r.occurredOn ?? r.scheduledFor ?? '';
  const past = active
    .filter((r) => !isUpcomingAppointment(r, today))
    .sort((a, b) => pastDate(b).localeCompare(pastDate(a))); // newest first, undated last
  return { upcoming, recentPast: past[0] ?? null, olderPast: past.slice(1) };
}

export function AppointmentsCard({
  rows,
  providers = [],
  clientName = null,
}: {
  rows: AppointmentRow[];
  /** Care-providers projection — resolves provider mentions to care-team
   *  rows (directory care-web; the Care record passes it). */
  providers?: readonly ProviderRow[];
  /** The name the visit-or-appointment dialog's title calls the client. */
  clientName?: string | null;
}): ReactNode {
  // Soak #2 fix: consume the injected day (deterministic under test) with
  // the practice's day as the fallback, the same discipline the page uses.
  const today = useToday() ?? practiceTodayIso();
  const { tenantSlug: visitSlug = '', clientId = '' } = useParams<{ tenantSlug: string; clientId: string }>();
  // Planned care-manager visits (0240): on their date and before it, never after.
  const visits = useCareVisits(visitSlug, clientId, today);
  const upcomingVisits = (visits.data ?? []).filter((v) => visitStillAhead(v, today));
  const canWriteAppointments = canWriteClinicalRecord(useTenant().whoami);
  const [addOpen, setAddOpen] = useState(false);
  const reveal = useArchivedReveal(rows);
  const [pastRevealed, setPastRevealed] = useState(false);
  const { upcoming, recentPast, olderPast } = partitionAppointments(reveal.active, today);
  const visibleActive = [
    ...upcoming,
    ...(recentPast !== null ? [recentPast] : []),
    ...(pastRevealed ? olderPast : []),
  ];
  const renderRow = (r: AppointmentRow): ReactNode => {
    // G2 (Soak #2 Wave B): the cadence tag — a NEUTRAL category chip (never
    // a register-signal color); ONE row represents the recurring series.
    const recurringTag = recurrenceTagLabel(r.recurrence);
    // 0233: a set place reads as its name and address; else the text.
    const place = appointmentPlaceLines(r);
    // The linked care-team row opens as the provider's pick. Edit and Add
    // a note open the same form.
    const editValues = {
      ...registryValuesFromRow({ ...r }, 'appointments'),
      ...(r.providerId ? { provider_id: r.providerId } : {}),
      ...appointmentPlaceValues(r),
    };
    return (
      <ProjectionRow
        envelope={r}
        title={<AppointmentProviderTitle row={r} providers={providers} />}
        // A recurring series is never "Past" (it repeats) — the caution
        // pill would contradict its own "Weekly recurring" chip (0144).
        pill={
          !isRecurring(r) && appointmentDatePassed(r, today)
            ? APPT_DATE_PASSED_PILL
            : APPT_STATUS_PILL[r.status]
        }
        edit={{ projection: 'appointments', values: editValues }}
      >
        {recurringTag !== null && (
          <div className="mb-1">
            <Chip variant="neutral" data-testid="appointment-recurrence-tag">
              {recurringTag}
            </Chip>
          </div>
        )}
        <FieldList
          fields={[
            { label: 'When', value: appointmentDate(r, today) },
            // G2 (Soak #2 Wave B): where the appointment happens (plain
            // PII); 0233: a set place on its two lines.
            ...(place.text === null
              ? [
                  { label: 'Location', value: place.name ?? undefined },
                  { label: 'Address', value: place.address ?? undefined },
                ]
              : [{ label: 'Location', value: place.text }]),
            { label: 'Purpose', value: shown(r.purpose, 'appointment_purpose') },
            { label: recordFieldLabel('appointments', 'outcome'), value: shown(r.outcome) },
          ]}
        />
        {appointmentAwaitsNote(r, today) && <AppointmentAddNote rowId={r.id} values={editValues} />}
      </ProjectionRow>
    );
  };
  return (
    <>
    <ProjectionCard
      archivedShown={reveal.showing}
      title="Appointments"
      testId="card-appointments"
      id="appointments"
      // PART 4: mist — matches the "Appointment" Latest-activity tag.
      accent={CARD_ACCENTS.appointments}
      emptyLabel="Add the first appointment. You'll also see appointments here once notes mention them."
      // A1: the Care tab's second budgeted heavy add (see medications).
      // One door (LP-42): "+ Visit or appointment" opens the dialog on its
      // Appointment face, with the Visit | Appointment switch at its top.
      // Named "Add a visit or appointment" so a screen reader hears the verb
      // every other card door carries; the name holds the visible words.
      // The card owns whether the dialog is open,
      // so a card that redraws (its first visit or row arriving) never
      // closes it.
      action={
        canWriteAppointments ? (
          <Button
            variant="primary"
            size="sm"
            icon={<Plus size={14} aria-hidden="true" />}
            data-testid="card-add"
            aria-label="Add a visit or appointment"
            onClick={() => setAddOpen(true)}
          >
            Visit or appointment
          </Button>
        ) : undefined
      }
    >
      {[
        ...upcomingVisits.map((v) => <CareVisitRow key={`visit-${v.id}`} visit={v} clientId={clientId} />),
        ...visibleActive.map((r) => <Fragment key={r.id}>{renderRow(r)}</Fragment>),
        ...(olderPast.length > 0
          ? [
              <li key="past-foot" className="list-none pt-0.5">
                <Button
                  variant="ghost"
                  size="sm"
                  className="!min-h-0 !px-1.5 !py-0.5 text-[12.5px]"
                  data-testid="past-appointments-toggle"
                  aria-pressed={pastRevealed}
                  onClick={() => setPastRevealed((v) => !v)}
                >
                  {pastRevealed
                    ? 'Hide past appointments'
                    : `Past appointments (${olderPast.length}) · Show`}
                </Button>
              </li>,
            ]
          : []),
        ...(reveal.revealed
          ? reveal.archived.map((r) => <Fragment key={r.id}>{renderRow(r)}</Fragment>)
          : []),
      ]}
    </ProjectionCard>
    {addOpen && <VisitOrAppointmentDialog clientId={clientId} clientName={clientName} face="appointment" onClose={() => setAddOpen(false)} />}
    </>
  );
}

// =====================================================================
// 4. contact — flag toggles (POA / HCP / emergency / lives-with).
// =====================================================================

function ContactFlags({ row, pronouns }: { row: ContactRow; pronouns: ClientPronouns | null }): ReactNode {
  // Hook stays ABOVE the flags-empty early return (#310 discipline) — the
  // billing-contact line only matters once a flag actually renders.
  const canSeeInvoice = canViewInvoice(useTenant().whoami);
  // The roles in the one map's words and the contact form's order.
  const roleOn: Record<ContactRoleKey, boolean> = {
    is_emergency: row.isEmergency,
    lives_with: row.livesWith,
    is_poa: row.isPoa,
    is_hcp: row.isHcp,
    is_guardian: row.isGuardian,
    is_billing: row.isBilling,
  };
  const flags: { label: string; on: boolean }[] = CONTACT_ROLE_KEYS.map((k) => ({
    label: contactRoleWord(k, pronouns),
    on: roleOn[k],
  }));
  // Soak #2 #14: show ONLY confirmed relationships — the off "○" chips
  // were noise. Nothing renders when every flag is off. Toggling stays
  // available in the edit dialog, so no relationship is stranded.
  const on = flags.filter((f) => f.on);
  if (on.length === 0) return null;
  return (
    <div className="space-y-1" data-testid="contact-flags">
      <div className="flex flex-wrap gap-1.5">
        {on.map((f) => (
          <Chip key={f.label} variant="affirm" data-flag={f.label} data-on="true">
            ✓ {f.label}
          </Chip>
        ))}
      </div>
      {/* 0149 (G8): when this contact is the billing designation, make the
          People<->Pre-billing link explicit — only for a viewer who can
          open Pre-billing at all (view_invoice); naming an invoice detail
          to someone with no invoice access is a dangling reference. */}
      {row.isBilling && canSeeInvoice && (
        <p className="text-xs text-ink-soft" data-testid="contact-billing-help">
          Their details fill the invoice bill-to on Pre-billing.
        </p>
      )}
    </div>
  );
}

export function ContactsCard({ rows, pronouns = null }: { rows: ContactRow[]; pronouns?: ClientPronouns | null }): ReactNode {
  const reveal = useArchivedReveal(rows);
  return (
    <ProjectionCard
      archivedShown={reveal.showing}
      title="Contacts"
      testId="card-contacts"
      emptyLabel="Add the first contact. You'll also see family and support contacts here once notes mention them."
      // A1: People's two cards are its anchors — both keep the heavy add.
      action={<AddRowAction projection="contacts" emphasis="primary" />}
    >
      {archivedAwareRows(reveal, (r) => (
        <ProjectionRow
          envelope={r}
          title={r.name}
          pill={r.relationship ? { label: r.relationship, variant: 'neutral' } : null}
          edit={{
            projection: 'contacts',
            values: registryValuesFromRow({ ...r }, 'contacts'),
          }}
        >
          <div className="space-y-1">
            {(() => {
              // The contact's own values, else its Directory entry's (0227).
              const shown = contactEffective(r);
              return (
                <FieldList
                  fields={[
                    { label: 'Phone', value: fmtPhone(shown.phone ?? undefined) },
                    { label: 'Email', value: shown.email ?? undefined },
                    { label: 'Address', value: shown.address ?? undefined },
                  ]}
                />
              );
            })()}
            <ContactFlags row={r} pronouns={pronouns} />
          </div>
        </ProjectionRow>
      ))}
    </ProjectionCard>
  );
}

// =====================================================================
// 5. provider — 3-arm linked entity (individual ↗dir / org ↗dir /
//    pending-steward / unlinked-by-name).
// =====================================================================

function ProviderLinkAffordance({ row }: { row: ProviderRow }): ReactNode {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  // After-the-fact linking for the unlinked arm (directory care-web).
  // Hook stays ABOVE the arms (rules-of-hooks; the #310 discipline).
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  // Linking is a record-row edit (PATCH .../record/providers/{id}), so the
  // door follows the row's own Edit (canWriteClinicalRecord); a reader
  // keeps the "Unlinked (by name)" chip (LP-48).
  const canLink = canWriteClinicalRecord(useTenant().whoami);
  const link = row.link;
  const linkCls =
    'inline-flex items-center gap-1 text-[11px] font-medium text-sage-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded-tight';

  if (link.kind === 'individual') {
    return (
      <Link
        to={`/t/${tenantSlug ?? ''}/individuals/${link.id}`}
        data-testid="provider-link"
        data-link-kind="individual"
        className={linkCls}
      >
        Directory profile <ExternalLink size={12} aria-hidden="true" />
      </Link>
    );
  }
  if (link.kind === 'org') {
    return (
      <Link
        to={`/t/${tenantSlug ?? ''}/organizations/${link.id}`}
        data-testid="provider-link"
        data-link-kind="org"
        className={linkCls}
      >
        Directory profile <ExternalLink size={12} aria-hidden="true" />
      </Link>
    );
  }
  if (link.kind === 'pending') {
    return (
      <Chip variant="caution" data-testid="provider-link" data-link-kind="pending">
        Steward review pending
      </Chip>
    );
  }
  // unlinked — the by-name row offers the after-the-fact Directory link
  // (directory care-web, founder feedback 2026-07-10): the same
  // search-first flow the add dialog leads with, one decision.
  return (
    <span className="inline-flex items-center gap-2">
      <Chip variant="neutral" data-testid="provider-link" data-link-kind="unlinked">
        Unlinked (by name)
      </Chip>
      {canLink && (
        <Button
          variant="ghost"
          size="sm"
          data-testid="provider-link-to-directory"
          icon={<ExternalLink size={12} aria-hidden="true" />}
          onClick={() => setLinkDialogOpen(true)}
        >
          Link to the Directory
        </Button>
      )}
      {linkDialogOpen && (
        <LinkProviderDialog
          rowId={row.id}
          displayName={row.displayName}
          onClose={() => setLinkDialogOpen(false)}
        />
      )}
    </span>
  );
}

/** The Care providers card's three groups, in the order they read. */
const PROVIDER_GROUPS = [
  { key: 'not-in-directory', heading: 'Not in the Directory', rows: (g: CareProviderGroups) => g.notInDirectory },
  { key: 'people', heading: 'People', rows: (g: CareProviderGroups) => g.people },
  { key: 'organizations', heading: 'Organizations', rows: (g: CareProviderGroups) => g.organizations },
] as const;

/**
 * A care-team row's edit (WS-5, 0217). A row linked to the Directory says
 * so, links to its Directory page, and shows the Directory's phone and
 * address as greyed placeholders where the row has none of its own, so the
 * form never reads as blank. The name box opens on the shown name; a name
 * this client typed for the person carries a hint on how to return to the
 * Directory's. Only an edited field is sent.
 */
export function providerEdit(r: ProviderRow, tenantSlug: string): RowEditConfig {
  // 0234: the office address opens on its parts; a text-only row's text
  // rides in from the registry values ("On file as" over empty fields).
  const base: RowEditConfig = {
    projection: 'providers',
    values: {
      ...registryValuesFromRow({ ...r }, 'providers'),
      ...(r.officeAddressParts !== undefined ? { office_address: r.officeAddressParts } : {}),
    },
  };
  const directoryTo =
    r.link.kind === 'individual'
      ? `/t/${tenantSlug}/individuals/${r.link.id}`
      : r.link.kind === 'org'
        ? `/t/${tenantSlug}/organizations/${r.link.id}`
        : null;
  if (directoryTo === null) return base;
  // The link and the entry's values: a box with no value of the row's
  // own shows the Directory's, tagged, with the way back from an own one.
  return {
    ...base,
    values: {
      ...base.values,
      ...(r.link.kind === 'individual'
        ? { directory_individual_id: r.link.id }
        : r.link.kind === 'org'
          ? { directory_org_id: r.link.id }
          : {}),
      directory_name: r.directoryName ?? r.displayName,
      directory_phone: r.directoryPhone ?? '',
      directory_address: r.directoryAddress ?? '',
      directory_email: r.directoryEmail ?? '',
      // The entry's address parts, which the office address fields show,
      // tagged, while the row has none of its own.
      directory_address_line1: r.directoryAddressParts?.line1 ?? '',
      directory_address_line2: r.directoryAddressParts?.line2 ?? '',
      directory_address_city: r.directoryAddressParts?.city ?? '',
      directory_address_state: r.directoryAddressParts?.state ?? '',
      directory_address_zip: r.directoryAddressParts?.zip ?? '',
    },
    intro: `Linked to the Directory: ${r.directoryName ?? r.displayName}.`,
    introLink: { text: 'Open profile', to: directoryTo },
    ...(r.directoryName ? { emptyValue: { display_name: r.directoryName } } : {}),
    ...(r.nameIsOverride ? { fieldHelp: { display_name: "Your own name for this person. Clear it to use the Directory's." } } : {}),
  };
}

export function ProvidersCard({ rows }: { rows: ProviderRow[] }): ReactNode {
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();
  const reveal = useArchivedReveal(rows);
  const groups = groupCareProviders(rows);
  const headingId = useId();
  const renderRow = (r: ProviderRow): ReactNode => (
    // WS-5: rows edit + archive like any target-row projection (the
    // team's duplicate-doctor fix is archiving the extras). The
    // allowlist is the row's own content columns; the Directory
    // link stays its own affordance and specialty/organization stay
    // directory-derived (never in the edit dialog).
    <ProjectionRow
      envelope={r}
      title={r.displayName}
      edit={providerEdit(r, tenantSlug)}
    >
      <div className="space-y-1">
        <FieldList
          fields={[
            { label: 'Role', value: r.role },
            { label: 'Specialty', value: r.specialty },
            { label: 'Organization', value: r.organization },
            // Per-client override first; the linked Directory
            // entry's contact is the display fallback (WS-5).
            { label: 'Office phone', value: fmtPhone(r.officePhone ?? r.directoryPhone) },
            { label: 'Office address', value: r.officeAddress ?? r.directoryAddress },
            // 0231: the row's own email, else the Directory's.
            { label: 'Email', value: r.email ?? r.directoryEmail },
          ]}
        />
        <ProviderLinkAffordance row={r} />
      </div>
    </ProjectionRow>
  );
  // The active rows in their groups (Not in the Directory first, so a
  // typed name gets linked, then People, then Organizations), each A to
  // Z; a group with no rows has no heading. Archived rows keep the
  // reveal: beneath every group, in entry order, under no heading.
  const grouped = subListGroups(
    'providers',
    headingId,
    PROVIDER_GROUPS.map((g) => ({ key: g.key, heading: g.heading, rows: g.rows(groups) })),
    renderRow,
  );
  return (
    <ProjectionCard
      archivedShown={reveal.showing}
      title="Care providers"
      testId="card-providers"
      emptyLabel="Add the first care provider, or link one from the Directory."
      // The camera door sits beside the add, as on Medications (one photo
      // feeds both cards), so a paper list has its door on the card it
      // fills, empty or not (LP-29).
      action={
        <span className="flex flex-wrap items-center gap-1">
          <AddFromPhotoAction />
          <AddRowAction
            title="Add to the care team"
            emphasis="primary"
            dialog={(close) => <AddProviderDialog onClose={close} />}
          />
        </span>
      }
    >
      {[...grouped, ...archivedAwareRows(reveal, renderRow, [])]}
    </ProjectionCard>
  );
}

// =====================================================================
// 7. functional status — the tenth card (0122), COMPACT-TABLE form
//    (founder soak feedback 2026-07-06: the 18-ghost-row list dominated
//    the column — wrong). Now:
//      - CAPTURED rows only, as a compact table (Activity | Level |
//        Note; level as the chip) matching the assessment's grid feel,
//        in FUNCTIONAL_ACTIVITIES registry order (never hand-listed);
//      - NOTHING captured → the card shows a one-line empty state, not
//        18 ghost rows;
//      - once ANY row exists, uncaptured activities collapse to ONE
//        quiet summary line ("12 activities not yet captured") — an
//        EXPLICIT not_assessed row still renders (the known-unknowns
//        signal), only silent absences summarize;
//      - each captured row keeps the LIVE edit/archive wiring (the
//        'functional-status' token — register class, so an activity
//        change re-lands on the new key's row) + the header "+ Add".
// =====================================================================

// The level's chip, the same colour for every activity (LP-19): one map,
// src/lib/functionalLevel.ts, shared with the face sheet.

/** One captured functional-status table row — carries the same
 *  hover/focus/tap chrome-rest as ProjectionRow (U4). */
/** The functional-status table's columns: Activity, Level, the actions.
 *  A note spans all of them on its own line. */
const FS_COLUMNS = 3;

/**
 * The functional-status table's block layout, for a card under 24rem wide
 * (a phone, or large text), too narrow for three columns: the header
 * goes, the activity, its level and the actions share a wrapping line,
 * and the note runs full width beneath. Written out whole so Tailwind
 * finds every class.
 */
const FS_BLOCK = {
  table: '@max-[24rem]:block',
  head: '@max-[24rem]:hidden',
  body: '@max-[24rem]:block @max-[24rem]:py-1',
  line: '@max-[24rem]:flex @max-[24rem]:flex-wrap @max-[24rem]:items-center @max-[24rem]:gap-x-3',
  cell: '@max-[24rem]:block @max-[24rem]:pr-0',
  actions: '@max-[24rem]:ml-auto @max-[24rem]:block',
  noteLine: '@max-[24rem]:block',
  note: '@max-[24rem]:block @max-[24rem]:pr-0',
} as const;

/** One activity: a tbody, so the note can take its own full-width line
 *  under the activity, the level and the actions. An activity with no
 *  note is one line. */
function FunctionalStatusRowTr({ row: r }: { row: FunctionalStatusRow }): ReactNode {
  const muted = !r.isActive;
  const pill = FUNCTIONAL_LEVEL_PILL[r.level];
  const hasNote = r.note !== undefined && r.note.trim() !== '';
  return (
    <tbody
      data-testid="projection-row"
      data-row-id={r.id}
      data-activity={r.activity}
      data-archived={muted ? 'true' : 'false'}
      className={`border-t border-sage-800/[6%] first-of-type:border-t-0 ${FS_BLOCK.body} ${muted ? 'opacity-60' : ''}`}
    >
      <tr className={FS_BLOCK.line}>
        <td className={`py-1.5 pr-3 align-top font-semibold text-sage-800 ${FS_BLOCK.cell}`}>
          {valueLabel('functional-status', 'activity', r.activity)}
        </td>
        <td className={`py-1.5 pr-3 align-top ${FS_BLOCK.cell}`}>
          <span className="inline-flex flex-wrap items-center gap-1">
            <Chip variant={pill.variant}>{pill.label}</Chip>
            {muted && <Chip variant="neutral">Archived</Chip>}
          </span>
        </td>
        <td className={`py-1 align-top ${FS_BLOCK.actions}`}>
          <RowActions
            edit={{
              projection: 'functional-status',
              values: registryValuesFromRow({ ...r }, 'functional-status'),
            }}
            rowId={r.id}
            isActive={r.isActive}
            archivable
          />
        </td>
      </tr>
      {hasNote && (
        <tr className={FS_BLOCK.noteLine}>
          <td colSpan={FS_COLUMNS} className={`whitespace-pre-wrap pb-1.5 pr-3 align-top text-ink-soft ${FS_BLOCK.note}`}>
            {r.note}
          </td>
        </tr>
      )}
    </tbody>
  );
}

/** A6: the uncaptured tail is an INVITATION, not a debt notice — the
 *  line is itself the add affordance (same registry-derived dialog). */
function TrackMoreActivities({ uncaptured }: { uncaptured: number }): ReactNode {
  const [open, setOpen] = useState(false);
  // The "track more" tail is a functional-status add door — hidden for a
  // viewer who does not keep the clinical record.
  const canWrite = canWriteClinicalRecord(useTenant().whoami);
  if (!canWrite) return null;
  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="mt-2 !px-1 !min-h-0 !py-0.5 text-[12px]"
        data-testid="fs-uncaptured-summary"
        onClick={() => setOpen(true)}
      >
        Track more activities · {uncaptured} not yet captured
      </Button>
      {open && (
        <RowEditDialog
          mode="create"
          projection="functional-status"
          rowId=""
          values={{}}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

export function FunctionalStatusCard({ rows }: { rows: FunctionalStatusRow[] }): ReactNode {
  // Item 5: archived rows leave the table by default; the page's archived
  // line appends them BENEATH the active rows. An activity whose only row is archived
  // counts as not-yet-captured (archived = removed from the record).
  const reveal = useArchivedReveal(rows);
  const byActivity = new Map(reveal.active.map((r) => [r.activity as string, r]));
  // Registry order for captured rows (never the wire order).
  const captured = FUNCTIONAL_ACTIVITIES.filter((a) => byActivity.has(a)).map(
    (a) => byActivity.get(a)!,
  );
  const uncaptured = FUNCTIONAL_ACTIVITIES.length - captured.length;
  const shown = [...captured, ...(reveal.revealed ? reveal.archived : [])];
  return (
    <ProjectionCard
      archivedShown={reveal.showing}
      title="Functional status"
      testId="card-functional-status"
      id="functional-status"
      flow="block"
      // PART 4: heather (memory) — matches the "Health" Latest-activity tag.
      accent={CARD_ACCENTS.memory}
      emptyLabel="Once an assessment or note records functional status, you'll see it here."
      action={<AddRowAction projection="functional-status" />}
    >
      {shown.length === 0 ? null : (
        <div>
          <div className="@container overflow-x-auto">
            <table className={`w-full text-sm ${FS_BLOCK.table}`} data-testid="fs-table">
              <thead className={FS_BLOCK.head}>
                <tr className="text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-sage-400">
                  <th className="py-1 pr-3 font-semibold">Activity</th>
                  <th className="py-1 pr-3 font-semibold">Level</th>
                  <th className="py-1">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              {shown.map((r) => (
                <FunctionalStatusRowTr key={r.id} row={r} />
              ))}
            </table>
          </div>
          {uncaptured > 0 && <TrackMoreActivities uncaptured={uncaptured} />}
        </div>
      )}
    </ProjectionCard>
  );
}
